/*
 * Sabre Digital: live delivery price in the product page delivery block.
 * Loaded only by sections/main-product.liquid.
 *
 * It asks Shopify what delivery would cost for the product being viewed, using the store's own
 * shipping settings, so the figures stay right when rates change. The lookup uses a separate,
 * throwaway cart through the Storefront API. The shopper's own basket is never touched.
 * If anything fails, the block keeps the plain wording set in the theme editor.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-pdp-delivery]');
  if (!root || !window.fetch) return;

  var titleEl = root.querySelector('[data-pdp-delivery-title]');
  var textEl = root.querySelector('[data-pdp-delivery-text]');
  var toggleEl = root.querySelector('[data-pdp-delivery-toggle]');
  var formEl = root.querySelector('[data-pdp-delivery-form]');
  var inputEl = root.querySelector('[data-pdp-delivery-input]');
  var closeEl = root.querySelector('[data-pdp-delivery-close]');
  if (!titleEl || !textEl) return;

  var cfg = {
    defaultZip: root.getAttribute('data-default-zip') || '',
    area: root.getAttribute('data-area') || 'the UK',
    timeStandard: root.getAttribute('data-time-standard') || '',
    timeExpress: root.getAttribute('data-time-express') || ''
  };
  if (!cfg.defaultZip) return;

  var API_URL = '/api/2026-07/graphql.json';
  var ZIP_KEY = 'sabrePdpPostcode';
  var CACHE_PREFIX = 'sabrePdpRates:';
  var CACHE_MINUTES = 30;

  var OPTIONS_FRAGMENT =
    'deliveryGroups(first: 5) { edges { node { deliveryOptions { title estimatedCost { amount currencyCode } } } } }';
  var CREATE_QUERY =
    'mutation ($input: CartInput!) { cartCreate(input: $input) { cart { id ' + OPTIONS_FRAGMENT + ' } userErrors { message } } }';
  var CART_QUERY = 'query ($id: ID!) { cart(id: $id) { id ' + OPTIONS_FRAGMENT + ' } }';

  function store(kind) {
    try {
      return window[kind];
    } catch (e) {
      return null;
    }
  }

  function readSession(key) {
    var s = store('sessionStorage');
    try {
      return s ? s.getItem(key) : null;
    } catch (e) {
      return null;
    }
  }

  function writeSession(key, value) {
    var s = store('sessionStorage');
    try {
      if (s) s.setItem(key, value);
    } catch (e) {}
  }

  function variantId() {
    var field =
      document.querySelector('[data-product-id-field]') ||
      document.querySelector('form[action*="/cart/add"] [name="id"]');
    return field && field.value ? String(field.value) : '';
  }

  function money(amount, currency) {
    var value = parseFloat(amount);
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(value);
    } catch (e) {
      return '£' + value.toFixed(2);
    }
  }

  /* "so509pd" becomes "SO50 9PD". An outward code alone ("EH1") is accepted too. */
  function tidyPostcode(raw) {
    var value = String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!/^[A-Z]{1,2}[0-9][A-Z0-9]?([0-9][A-Z]{2})?$/.test(value)) return '';
    return value.length > 4 ? value.slice(0, -3) + ' ' + value.slice(-3) : value;
  }

  function post(query, variables) {
    return fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.json();
    });
  }

  function optionsFrom(cart) {
    var list = [];
    var edges = (cart && cart.deliveryGroups && cart.deliveryGroups.edges) || [];
    edges.forEach(function (edge) {
      ((edge.node && edge.node.deliveryOptions) || []).forEach(function (option) {
        if (!option || !option.estimatedCost) return;
        list.push({
          title: String(option.title || '').trim(),
          amount: parseFloat(option.estimatedCost.amount),
          currency: option.estimatedCost.currencyCode
        });
      });
    });
    return list;
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function createCart(variant, zip, legacyShape) {
    var input = {
      lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variant, quantity: 1 }],
      buyerIdentity: { countryCode: 'GB' }
    };
    if (legacyShape) {
      input.buyerIdentity.deliveryAddressPreferences = [{ deliveryAddress: { country: 'GB', zip: zip } }];
    } else {
      input.delivery = { addresses: [{ selected: true, address: { deliveryAddress: { countryCode: 'GB', zip: zip } } }] };
    }
    return post(CREATE_QUERY, { input: input }).then(function (json) {
      if (json.errors && !legacyShape) return createCart(variant, zip, true);
      if (json.errors) throw new Error('Storefront API error');
      var cart = json.data && json.data.cartCreate && json.data.cartCreate.cart;
      if (!cart) throw new Error('No cart returned');
      return cart;
    });
  }

  /* Rates can take a moment to calculate, so an empty answer is checked once more. */
  function fetchOptions(variant, zip) {
    return createCart(variant, zip, false).then(function (cart) {
      var options = optionsFrom(cart);
      if (options.length) return options;
      return wait(900)
        .then(function () {
          return post(CART_QUERY, { id: cart.id });
        })
        .then(function (json) {
          return optionsFrom(json.data && json.data.cart);
        });
    });
  }

  function lookup(variant, zip) {
    var cacheKey = CACHE_PREFIX + variant + ':' + zip;
    var cached = readSession(cacheKey);
    if (cached) {
      try {
        var parsed = JSON.parse(cached);
        if (parsed && Date.now() - parsed.at < CACHE_MINUTES * 60000) return Promise.resolve(parsed.options);
      } catch (e) {}
    }
    return fetchOptions(variant, zip).then(function (options) {
      writeSession(cacheKey, JSON.stringify({ at: Date.now(), options: options }));
      return options;
    });
  }

  function byTitle(options, name) {
    for (var i = 0; i < options.length; i++) {
      if (options[i].title.toLowerCase() === name) return options[i];
    }
    return null;
  }

  /* "Boxed Delivery (Assembly required) – Fastest & Best Value" becomes "Boxed Delivery (Assembly required)" */
  function shortTitle(title) {
    return title.split(/\s[–—-]\s/)[0].trim();
  }

  function render(options, area) {
    var standard = byTitle(options, 'standard');
    var express = byTitle(options, 'express');
    var heading;
    var text;

    if (standard) {
      heading = standard.amount === 0 ? 'Free standard delivery' : 'Standard delivery ' + money(standard.amount, standard.currency);
      text = (cfg.timeStandard ? cfg.timeStandard.charAt(0).toUpperCase() + cfg.timeStandard.slice(1) + ' to ' : 'Delivered to ') + area + '.';
      if (express) {
        text += ' Express ' + money(express.amount, express.currency) + (cfg.timeExpress ? ', ' + cfg.timeExpress : '') + '.';
      }
    } else {
      var cheapest = options.slice().sort(function (a, b) {
        return a.amount - b.amount;
      })[0];
      heading = cheapest.amount === 0 ? 'Free delivery' : 'Delivery from ' + money(cheapest.amount, cheapest.currency);
      text = shortTitle(cheapest.title) + ' to ' + area + '.';
      if (options.length > 1) text += ' More options at checkout.';
    }

    titleEl.textContent = heading;
    textEl.textContent = text;
  }

  var busy = false;

  function run(zip, isDefault) {
    var variant = variantId();
    if (!variant || busy) return Promise.resolve();
    busy = true;
    root.setAttribute('data-state', 'loading');

    return lookup(variant, zip)
      .then(function (options) {
        if (options.length) {
          render(options, isDefault ? cfg.area : zip);
          if (toggleEl) toggleEl.textContent = isDefault ? 'Check postcode' : 'Change postcode';
          if (!isDefault) writeSession(ZIP_KEY, zip);
        } else if (!isDefault) {
          titleEl.textContent = 'Delivery to ' + zip;
          textEl.textContent = 'We could not find a delivery price for that postcode. Please check it, or contact us.';
        }
        root.setAttribute('data-state', 'ready');
      })
      .catch(function () {
        /* Leave the plain wording from the theme editor in place. */
        root.setAttribute('data-state', 'error');
      })
      .then(function () {
        busy = false;
        if (toggleEl) toggleEl.hidden = false;
      });
  }

  function start() {
    var saved = tidyPostcode(readSession(ZIP_KEY));
    if (saved) {
      if (inputEl) inputEl.value = saved;
      run(saved, false);
    } else {
      run(tidyPostcode(cfg.defaultZip) || cfg.defaultZip, true);
    }
  }

  function showForm(show) {
    formEl.hidden = !show;
    toggleEl.setAttribute('aria-expanded', show ? 'true' : 'false');
    if (show && inputEl) {
      inputEl.focus();
      inputEl.select();
    }
  }

  if (toggleEl && formEl) {
    toggleEl.addEventListener('click', function () {
      showForm(formEl.hidden);
    });

    if (closeEl) {
      closeEl.addEventListener('click', function () {
        showForm(false);
        toggleEl.focus();
      });
    }

    formEl.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        showForm(false);
        toggleEl.focus();
      }
    });

    formEl.addEventListener('submit', function (event) {
      event.preventDefault();
      var zip = tidyPostcode(inputEl && inputEl.value);
      if (!zip) {
        textEl.textContent = 'Please enter a UK postcode, for example SO50 9PD.';
        if (inputEl) inputEl.focus();
        return;
      }
      if (inputEl) inputEl.value = zip;
      run(zip, false).then(function () {
        showForm(false);
      });
    });
  }

  /* Only look the price up once the block is close to being seen. */
  if ('IntersectionObserver' in window) {
    var observer = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          start();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(root);
  } else {
    start();
  }
})();

/*
 * Delivery and returns pop-ups. The links open a short summary on the product page
 * instead of sending the shopper to another page. Each pop-up links to the full page.
 */
(function () {
  'use strict';

  var triggers = document.querySelectorAll('[data-pdp-pop]');
  if (!triggers.length) return;

  function lockPage(lock) {
    document.documentElement.classList[lock ? 'add' : 'remove']('pdp-pop-open');
  }

  Array.prototype.forEach.call(triggers, function (trigger) {
    trigger.addEventListener('click', function (event) {
      var dialog = document.getElementById('pdp-pop-' + trigger.getAttribute('data-pdp-pop'));
      /* No pop-up available, or an old browser: let the link go to the full page as normal. */
      if (!dialog || typeof dialog.showModal !== 'function') return;
      event.preventDefault();
      dialog.showModal();
      lockPage(true);
    });
  });

  Array.prototype.forEach.call(document.querySelectorAll('dialog.pdp-pop'), function (dialog) {
    dialog.addEventListener('click', function (event) {
      /* A click on the dimmed area lands on the dialog element itself. */
      var onBackdrop = event.target === dialog;
      var onClose = event.target.closest && event.target.closest('[data-pdp-pop-close]');
      if (onBackdrop || onClose) {
        dialog.close();
        lockPage(false);
      }
    });
    dialog.addEventListener('close', function () {
      lockPage(false);
    });
  });
})();

/*
 * "Complete your order" add-ons under the buy buttons.
 * Each add-on goes into the basket from the product page, so the shopper never leaves it.
 *  - Nothing to choose: one tap adds it. A second tap takes it back out.
 *  - Colours or sizes: a pop-up asks first, then adds the chosen one.
 * The basket count in the header and the slide-out basket are refreshed, and the slide-out basket
 * opens after an add, the same as it does for the main Add To Basket button. Taking an add-on back
 * out does not open it.
 * The theme loads the add-ons after the page, so everything here listens on the wrapper around them.
 */
(function () {
  'use strict';

  var wrapper = document.querySelector('[data-complementary-products]');
  if (!wrapper || !window.fetch) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  var ownRefreshUntil = 0;
  var lastCart = null;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function request(path, body) {
    var options = { credentials: 'same-origin', headers: body ? JSON_HEADERS : { Accept: 'application/json' } };
    if (body) {
      options.method = 'POST';
      options.body = JSON.stringify(body);
    }
    return fetch(ROOT + path, options).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) throw new Error((json && (json.description || json.message)) || 'Request failed');
        return json;
      });
    });
  }

  function getCart() {
    return request('cart.js');
  }

  /* ---------- Row state ---------- */

  function rowIds(row) {
    return (row.getAttribute('data-addon-ids') || '').split(',').filter(Boolean);
  }

  function setRow(row, state) {
    var button = row.querySelector('.pdp-addon__btn');
    var label = row.querySelector('[data-addon-btn-label]');
    if (!button || !label) return;
    var title = row.getAttribute('data-addon-title') || '';
    var idle = button.getAttribute('data-label') || 'Add';

    row.setAttribute('data-state', state);
    button.disabled = state === 'loading';

    if (state === 'added') {
      label.textContent = 'Added';
      button.setAttribute('aria-label', title + ': added. Tap to remove from basket');
    } else if (state === 'idle') {
      label.textContent = idle;
      button.setAttribute('aria-label', (idle === 'Add' ? 'Add to basket: ' : 'Choose options for ') + title);
    }
  }

  function lineFor(row, cart) {
    var ids = rowIds(row);
    var found = null;
    (cart.items || []).forEach(function (item) {
      if (!found && ids.indexOf(String(item.variant_id)) > -1) found = item;
    });
    return found;
  }

  function setCount(count) {
    each(document.querySelectorAll('[data-cart-count]'), function (badge) {
      badge.innerHTML = count;
      badge.classList[count > 0 ? 'add' : 'remove']('active');
    });
  }

  function sync(cart) {
    lastCart = cart;
    each(wrapper.querySelectorAll('[data-addon]'), function (row) {
      if (row.getAttribute('data-state') === 'loading') return;
      setRow(row, lineFor(row, cart) ? 'added' : 'idle');
    });
    setCount(cart.item_count);
  }

  /* The header count goes up straight away. The real basket is read in the background to confirm it. */
  function bumpCount() {
    var badge = document.querySelector('[data-cart-count]');
    var current = badge ? parseInt(badge.textContent, 10) : NaN;
    if (!isNaN(current)) setCount(current + 1);
  }

  function say(text) {
    var status = wrapper.querySelector('[data-addons-status]');
    if (status) status.textContent = text;
  }

  function showRowMessage(row, text) {
    var message = row.querySelector('[data-addon-msg]');
    if (!message) return;
    message.textContent = text;
    message.hidden = false;
    clearTimeout(message._timer);
    message._timer = setTimeout(function () {
      message.hidden = true;
    }, 6000);
  }

  /* ---------- Slide-out basket ---------- */

  /* The theme's own styling for the quantity dropdowns in the basket, applied the way the theme applies it. */
  function styleFields(box, slate) {
    if (!window.jcf || !window.jcf.replaceAll) return;
    each(box.querySelectorAll('select:not(.jcf-ignore)'), function (select) {
      select.setAttribute(
        'data-jcf',
        JSON.stringify({ wrapNative: false, wrapNativeOnMobile: false, fakeDropInBody: false, multipleCompactStyle: true })
      );
    });
    window.jcf.replaceAll(box);
    each(box.querySelectorAll('.jcf-icon'), function (icon) {
      icon.parentNode.removeChild(icon);
    });
    var caret = (slate.svgs && slate.svgs.caretDown) || '';
    each(box.querySelectorAll('.jcf-select-opener'), function (opener) {
      opener.insertAdjacentHTML('beforeend', '<span class="jcf-icon">' + caret + '</span>');
    });
  }

  /* Opens the slide-out basket through the theme's own basket link in the header. */
  function openBasket() {
    var link = document.querySelector('[data-popover-target="cart"]');
    if (link) link.click();
  }

  /*
   * Puts fresh basket HTML into the slide-out basket, with the same steps the theme takes after
   * Add To Basket. The basket opens only when asked to.
   */
  function fillMiniCart(html, open) {
    var slate = window.Slate;
    var box = slate && slate.miniCart && slate.miniCart.container;
    if (!box || !html) return false;

    ownRefreshUntil = Date.now() + 1500;
    box.innerHTML = html;

    try {
      styleFields(box, slate);
    } catch (e) {}

    /* The theme's own set-up for the quantity and remove controls in the basket. */
    try {
      (slate.callables || []).forEach(function (callable) {
        if (callable && callable.name === 'miniCart') callable();
      });
    } catch (e) {}

    if (open) openBasket();

    /* "Customers Also Bought" loads after the basket is already showing, so it never holds it up. */
    var extras = box.querySelector('[data-recommended-mini-products]');
    var productId = extras && extras.getAttribute('data-recommended-mini-products');
    if (productId) {
      fetch(
        ROOT +
          'recommendations/products?product_id=' +
          encodeURIComponent(productId) +
          '&section_id=recommended-mini-products-content&intent=related&limit=3'
      )
        .then(function (response) {
          return response.ok ? response.text() : '';
        })
        .then(function (text) {
          if (text) extras.innerHTML = text;
        })
        .catch(function () {});
    }
    return true;
  }

  /* Used only when Shopify did not send the basket HTML back with the add or remove. */
  function refreshMiniCart(open) {
    ownRefreshUntil = Date.now() + 2500;
    request('?sections=mini-cart')
      .then(function (data) {
        fillMiniCart(data && data['mini-cart'], open);
      })
      .catch(function () {});
  }

  function basketHtml(json) {
    return json && json.sections && json.sections['mini-cart'];
  }

  /* ---------- Add and remove ---------- */

  /*
   * One request adds the item and brings back the slide-out basket's HTML, so the basket opens as soon
   * as Shopify answers. Reading the whole basket (for the count and the other rows) happens afterwards.
   * sections_url matters: without it Shopify builds the basket HTML as if it were on this product page,
   * and the theme's price snippet then shows this product's price against every line.
   */
  function add(row, variantId) {
    return request('cart/add.js', {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: 'mini-cart',
      sections_url: ROOT
    }).then(function (json) {
      setRow(row, 'added');
      bumpCount();
      say((row.getAttribute('data-addon-title') || 'Item') + ' added to your basket.');
      if (!fillMiniCart(basketHtml(json), true)) refreshMiniCart(true);
      getCart().then(sync).catch(function () {});
    });
  }

  function remove(row) {
    setRow(row, 'loading');
    var known = lastCart && lineFor(row, lastCart) ? Promise.resolve(lastCart) : getCart();
    known
      .then(function (cart) {
        var line = lineFor(row, cart);
        if (!line) return cart;
        return request('cart/change.js', {
          id: line.key,
          quantity: Math.max(0, line.quantity - 1),
          sections: 'mini-cart',
          sections_url: ROOT
        });
      })
      .then(function (cart) {
        setRow(row, 'idle');
        sync(cart);
        if (!fillMiniCart(basketHtml(cart), false)) refreshMiniCart(false);
        say((row.getAttribute('data-addon-title') || 'Item') + ' removed from your basket.');
      })
      .catch(function () {
        setRow(row, 'added');
        showRowMessage(row, 'Sorry, we could not update your basket. Please try again.');
      });
  }

  function addFromRow(row, variantId) {
    setRow(row, 'loading');
    add(row, variantId).catch(function (error) {
      setRow(row, 'idle');
      showRowMessage(row, (error && error.message) || 'Sorry, we could not add this. Please try again.');
    });
  }

  /* ---------- Pop-up with colours and sizes ---------- */

  function lockPage(lock) {
    document.documentElement.classList[lock ? 'add' : 'remove']('pdp-pop-open');
  }

  function visibleGroup(dialog) {
    var colour = dialog.querySelector('[data-addon-colour][aria-pressed="true"]');
    return colour ? dialog.querySelector('[data-addon-variants="' + colour.getAttribute('data-addon-colour') + '"]') : null;
  }

  function chosenVariant(dialog) {
    var group = visibleGroup(dialog);
    return group ? group.querySelector('[data-addon-variant][aria-pressed="true"]') : null;
  }

  function updateConfirm(dialog) {
    var confirm = dialog.querySelector('[data-addon-confirm]');
    var label = dialog.querySelector('[data-addon-confirm-label]');
    var price = dialog.querySelector('[data-addon-pop-price]');
    var sizeName = dialog.querySelector('[data-addon-size-name]');
    var colour = dialog.querySelector('[data-addon-colour][aria-pressed="true"]');
    var variant = chosenVariant(dialog);
    if (!confirm || !label) return;

    if (variant) {
      label.textContent = 'Add to basket';
      confirm.removeAttribute('aria-disabled');
      if (price) price.textContent = variant.getAttribute('data-price');
      if (sizeName) sizeName.textContent = variant.getAttribute('data-name');
    } else {
      label.textContent = 'Choose a ' + (confirm.getAttribute('data-size-label') || 'size');
      confirm.setAttribute('aria-disabled', 'true');
      if (price && colour) price.textContent = colour.getAttribute('data-price');
      if (sizeName) sizeName.textContent = '';
    }
  }

  function selectVariant(dialog, button) {
    var group = visibleGroup(dialog);
    if (!group) return;
    each(group.querySelectorAll('[data-addon-variant]'), function (option) {
      option.setAttribute('aria-pressed', option === button ? 'true' : 'false');
    });
    if (button) dialog._sizeName = button.getAttribute('data-name');
    var sizes = dialog.querySelector('[data-addon-sizes]');
    if (sizes) sizes.classList.remove('pdp-addon-pop__group--nudge');
    updateConfirm(dialog);
  }

  function selectColour(dialog, button) {
    each(dialog.querySelectorAll('[data-addon-colour]'), function (option) {
      option.setAttribute('aria-pressed', option === button ? 'true' : 'false');
    });

    var id = button.getAttribute('data-addon-colour');
    var group = null;
    each(dialog.querySelectorAll('[data-addon-variants]'), function (candidate) {
      var match = candidate.getAttribute('data-addon-variants') === id;
      candidate.hidden = !match;
      if (match) group = candidate;
    });

    var colourName = dialog.querySelector('[data-addon-colour-name]');
    var name = dialog.querySelector('[data-addon-pop-name]');
    var image = dialog.querySelector('[data-addon-pop-img]');
    var link = dialog.querySelector('[data-addon-pop-link]');
    if (colourName) colourName.textContent = button.getAttribute('data-name');
    if (name) name.textContent = button.getAttribute('data-title');
    if (image && button.getAttribute('data-img')) image.src = button.getAttribute('data-img');
    if (link && button.getAttribute('data-url')) link.href = button.getAttribute('data-url');

    var options = group ? group.querySelectorAll('[data-addon-variant]') : [];
    var sizes = dialog.querySelector('[data-addon-sizes]');
    if (sizes) sizes.hidden = options.length < 2;

    /* One size only: it is chosen already. Otherwise keep the size picked on another colour, if this colour has it. */
    var pick = null;
    each(options, function (option) {
      if (option.disabled) return;
      if (options.length === 1 || (dialog._sizeName && option.getAttribute('data-name') === dialog._sizeName)) pick = option;
    });
    selectVariant(dialog, pick);
  }

  function openPop(id) {
    var dialog = document.getElementById(id);
    if (!dialog) return;

    /* A browser too old for pop-ups: go to the add-on's own page instead, as the old links did. */
    if (typeof dialog.showModal !== 'function') {
      var fallback = dialog.querySelector('[data-addon-pop-link]');
      if (fallback && fallback.getAttribute('href')) window.location.href = fallback.getAttribute('href');
      return;
    }

    if (!dialog.querySelector('[data-addon-colour][aria-pressed="true"]')) {
      var first =
        dialog.querySelector('[data-addon-colour][data-default="true"]') || dialog.querySelector('[data-addon-colour]');
      if (first) selectColour(dialog, first);
    }

    var message = dialog.querySelector('[data-addon-pop-msg]');
    if (message) message.hidden = true;
    dialog.showModal();
    lockPage(true);
  }

  function confirmPop(dialog, row) {
    var confirm = dialog.querySelector('[data-addon-confirm]');
    var message = dialog.querySelector('[data-addon-pop-msg]');
    var variant = chosenVariant(dialog);

    if (!variant) {
      var sizes = dialog.querySelector('[data-addon-sizes]');
      if (sizes) {
        sizes.classList.remove('pdp-addon-pop__group--nudge');
        void sizes.offsetWidth;
        sizes.classList.add('pdp-addon-pop__group--nudge');
      }
      return;
    }

    if (confirm.getAttribute('data-state') === 'loading') return;
    confirm.setAttribute('data-state', 'loading');
    if (message) message.hidden = true;

    add(row, variant.getAttribute('data-addon-variant'))
      .then(function () {
        confirm.removeAttribute('data-state');
        dialog.close();
        lockPage(false);
      })
      .catch(function (error) {
        confirm.removeAttribute('data-state');
        if (message) {
          message.textContent = (error && error.message) || 'Sorry, we could not add this. Please try again.';
          message.hidden = false;
        }
      });
  }

  /* ---------- Events ---------- */

  wrapper.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var row = target.closest('[data-addon]');
    if (!row) return;

    var dialog = target.closest('dialog[data-addon-pop]');
    if (dialog) {
      var colour = target.closest('[data-addon-colour]');
      var variant = target.closest('[data-addon-variant]');
      if (target === dialog || target.closest('[data-pdp-pop-close]')) {
        dialog.close();
        lockPage(false);
      } else if (colour) {
        selectColour(dialog, colour);
      } else if (variant && !variant.disabled) {
        selectVariant(dialog, variant);
      } else if (target.closest('[data-addon-confirm]')) {
        confirmPop(dialog, row);
      }
      return;
    }

    var button = target.closest('.pdp-addon__btn');
    if (button) {
      if (row.getAttribute('data-state') === 'loading') return;
      if (row.getAttribute('data-state') === 'added') {
        remove(row);
      } else if (button.hasAttribute('data-addon-add')) {
        addFromRow(row, button.getAttribute('data-addon-add'));
      } else {
        openPop(button.getAttribute('data-addon-open'));
      }
      return;
    }

    var opener = target.closest('[data-addon-open]');
    if (opener) openPop(opener.getAttribute('data-addon-open'));
  });

  /* The close event does not bubble, so it is caught on the way down. */
  wrapper.addEventListener(
    'close',
    function (event) {
      if (event.target && event.target.matches && event.target.matches('dialog[data-addon-pop]')) lockPage(false);
    },
    true
  );

  /* Mark add-ons that are already in the basket, once the theme has loaded them in. */
  function syncFromBasket() {
    if (!wrapper.querySelector('[data-addon]')) return;
    getCart().then(sync).catch(function () {});
  }

  if ('MutationObserver' in window) {
    new MutationObserver(syncFromBasket).observe(wrapper, { childList: true });

    /* Keep the buttons right when the shopper changes the slide-out basket. */
    var box = document.querySelector('[data-mini-cart]');
    var timer;
    if (box) {
      new MutationObserver(function () {
        if (Date.now() < ownRefreshUntil) return;
        clearTimeout(timer);
        timer = setTimeout(syncFromBasket, 300);
      }).observe(box, { childList: true });
    }
  }

  syncFromBasket();
})();

/*
 * Product story: "At a glance" band and product information tabs.
 *
 * Garland's descriptions are long and already written under clear headings, but all of it sat inside one
 * closed "Description" row. This reads that content and presents it properly, on every product, with no
 * data entry:
 *
 *  1. At a glance: a dark band with the product's key facts as tiles, beside a product photo. The facts come
 *     from the "Quick Overview" block in the description, or from the first rows of the specification table
 *     where a product has no overview.
 *  2. Product information: Specifications, What's in the box, FAQs, Full description and Delivery & Returns.
 *     Tabs on desktop, with the specifications shown first. Rows that open and close on phones.
 *     Specification tables become a clean two column list, FAQs become questions that open one at a time.
 *
 * Nothing is rewritten or invented: the words are Garland's own, only moved and restyled. A product whose
 * description has no headings keeps its plain Description and Delivery & Returns rows.
 * Without JavaScript nothing moves and the page works as before.
 */
(function () {
  'use strict';

  var list = document.querySelector('.accordion--desc');
  if (!list) return;

  function slice(nodes) {
    return Array.prototype.slice.call(nodes);
  }

  function text(node) {
    return (node.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function make(tag, className, content) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (content) element.textContent = content;
    return element;
  }

  /* ---------- Rows that open and close (phones, and products without tabs) ---------- */

  /*
   * Taking the click here, before the theme sees it, means a row can never be toggled twice, whichever
   * script loaded first. Once a row has opened its height is released, so a question opened inside the
   * FAQs is never cut off.
   */
  function release(content) {
    if (content.classList.contains('active')) content.style.height = 'auto';
  }

  list.addEventListener(
    'click',
    function (event) {
      var title = event.target && event.target.closest ? event.target.closest('.accordion__item-title') : null;
      if (!title || !list.contains(title) || !title.hasAttribute('data-toggle-target')) return;

      event.preventDefault();
      event.stopPropagation();

      var name = title.getAttribute('data-toggle-target');
      var content = null;
      slice(list.querySelectorAll('[data-toggle]')).forEach(function (candidate) {
        if (candidate.getAttribute('data-toggle') === name) content = candidate;
      });
      if (!content) return;

      var open = !content.classList.contains('active');
      title.classList[open ? 'add' : 'remove']('active');
      title.setAttribute('aria-expanded', open ? 'true' : 'false');

      if (open) {
        content.style.height = content.scrollHeight + 'px';
        content.classList.add('active');
        setTimeout(function () {
          release(content);
        }, 320);
      } else {
        content.style.height = content.scrollHeight + 'px';
        void content.offsetHeight;
        content.classList.remove('active');
        content.style.height = '0px';
      }
    },
    true
  );

  /* The theme fixes the height of open rows again when the window changes size. Release them after it has. */
  list.addEventListener(
    'toggle',
    function () {
      slice(list.querySelectorAll('[data-toggle].active')).forEach(release);
    },
    true
  );
  window.addEventListener('resize', function () {
    setTimeout(function () {
      slice(list.querySelectorAll('[data-toggle].active')).forEach(release);
    }, 60);
  });

  /* ---------- Reading the description ---------- */

  var descContent = list.querySelector('[data-toggle="product-description"]');
  var inner = descContent && descContent.querySelector('.accordion__item-content-inner');
  var descItem = descContent && descContent.parentNode;
  if (!inner || !descItem) return;

  /* In the order the tabs appear. The heading is hidden where it would only repeat the tab's own title. */
  var ROWS = [
    { key: 'specs', title: 'Specifications', test: /specification/i },
    { key: 'box', title: 'What\u2019s in the box', test: /what.?s\s+in\s+the\s+box|what.?s\s+included/i },
    { key: 'faqs', title: 'FAQs', test: /\bfaqs?\b|frequently\s+asked/i }
  ];
  var OVERVIEW = /quick\s+overview/i;

  var headings = slice(inner.querySelectorAll('h2'));
  if (headings.length < 2) return;

  /* The element whose children are the description's sections (usually the description itself). */
  var root = headings[0].parentNode;
  while (root && root !== inner) {
    var holdsAll = headings.every(function (heading) {
      return root.contains(heading);
    });
    if (holdsAll) break;
    root = root.parentNode;
  }
  if (!root) root = inner;

  /*
   * A section is either a heading followed by what sits under it, up to the next heading or dividing
   * line, or a single block with one heading inside it (a card). Anything else stays in the description.
   */
  var groups = [];
  var current = null;

  slice(root.childNodes).forEach(function (node) {
    if (node.nodeType !== 1) {
      if (current) current.nodes.push(node);
      return;
    }
    if (node.tagName === 'HR') {
      current = null;
      return;
    }

    var isHeading = node.tagName === 'H2';
    var inside = isHeading ? [node] : slice(node.querySelectorAll('h2'));

    if (inside.length === 1) {
      current = { title: inside[0].textContent || '', heading: inside[0], nodes: [node], card: !isHeading };
      groups.push(current);
      if (!isHeading) current = null;
    } else if (inside.length > 1) {
      current = null;
    } else if (current) {
      current.nodes.push(node);
    }
  });

  function rowFor(title) {
    for (var i = 0; i < ROWS.length; i++) {
      if (ROWS[i].test.test(title)) return ROWS[i];
    }
    return null;
  }

  function createRow(row) {
    var id = 'pdp-row-' + row.key;
    var item = document.createElement('li');
    item.className = 'accordion__item';
    item.innerHTML =
      '<a class="accordion__item-title" href="#" data-toggle-target="' + id + '" aria-expanded="false">' +
      '<h3></h3><span class="accordion__item-icon"></span></a>' +
      '<div class="accordion__item-content" data-toggle="' + id + '">' +
      '<div class="accordion__item-content-inner txt-small txt-styles txt-styles--small pdp-row pdp-row--' + row.key + '"></div></div>';
    item.querySelector('h3').textContent = row.title;
    return { item: item, inner: item.querySelector('.accordion__item-content-inner') };
  }

  var made = {};
  var moved = 0;
  var overview = null;

  /* FAQs go into the questions section under the tabs when the page has one, otherwise into a tab. */
  var ask = document.querySelector('[data-pdp-ask]');
  var askFaqs = ask && ask.querySelector('[data-pdp-ask-faqs]');
  var askList = ask && ask.querySelector('[data-pdp-ask-list]');

  groups.forEach(function (group) {
    if (!overview && group.card && OVERVIEW.test(group.title)) {
      overview = group;
      return;
    }

    var row = rowFor(group.title);
    if (!row) return;

    var target = made[row.key];
    if (!target) {
      if (row.key === 'faqs' && askList) {
        askList.className += ' pdp-row pdp-row--faqs txt-small txt-styles txt-styles--small';
        target = made[row.key] = { item: null, inner: askList };
      } else {
        target = made[row.key] = createRow(row);
      }
      if (!group.card) group.heading.classList.add('pdp-row__heading-hidden');
    }

    group.nodes.forEach(function (node) {
      target.inner.appendChild(node);
    });
    moved++;
  });

  /* ---------- 1. At a glance ---------- */

  function tidyValue(value) {
    value = value.replace(/^[\s:\u2013\u2014-]+/, '').replace(/\.$/, '');
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  /* "Label: value" lines from the Quick Overview block. Returns null unless the block is only such lines. */
  function factsFromOverview(group) {
    var card = group.nodes[0];
    var facts = [];
    var notes = [];
    var clean = true;

    slice(card.children).forEach(function (child) {
      if (child === group.heading) return;
      var label = child.tagName === 'P' ? child.querySelector('strong') : null;
      if (!label || child.firstElementChild !== label && text(child).indexOf(text(label)) !== 0) {
        if (text(child)) clean = false;
        return;
      }
      var name = text(label).replace(/:\s*$/, '');
      var value = text(child).slice(text(label).length);
      if (!name || !value) {
        clean = false;
        return;
      }
      if (value.length > 80 || /^(our view|our take|verdict)$/i.test(name)) {
        notes.push({ label: name, value: value.replace(/^[\s:]+/, '') });
      } else {
        facts.push({ label: name, value: tidyValue(value) });
      }
    });

    return clean && facts.length >= 3 ? { facts: facts, notes: notes } : null;
  }

  /* The first rows of a two column specification table, for products with no overview block. */
  function factsFromSpecs(container) {
    var facts = [];
    slice(container.querySelectorAll('tr')).forEach(function (row) {
      if (facts.length >= 6 || row.children.length !== 2) return;
      var name = text(row.children[0]);
      var value = (row.children[1].innerHTML || '').split(/<br\s*\/?>/i)[0].replace(/<[^>]+>/g, ' ');
      value = value.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim();
      if (name && value && value.length <= 80) facts.push({ label: name, value: tidyValue(value) });
    });
    return facts.length >= 3 ? { facts: facts, notes: [] } : null;
  }

  function buildGlance(data, headingNode, titleText) {
    var band = make('section', 'pdp-glance');
    band.setAttribute('aria-label', 'At a glance');

    /* A second photo from the gallery, so the band does not repeat the main image. */
    var photos = slice(document.querySelectorAll('.product__imgs-slider img'));
    var photo = photos[1] || photos[0];
    if (photo && (photo.getAttribute('src') || photo.currentSrc)) {
      var media = make('div', 'pdp-glance__media');
      var image = document.createElement('img');
      image.src = photo.getAttribute('src') || photo.currentSrc;
      if (photo.getAttribute('srcset')) image.setAttribute('srcset', photo.getAttribute('srcset'));
      image.setAttribute('sizes', '(min-width: 769px) 450px, 100vw');
      image.alt = '';
      image.loading = 'lazy';
      media.appendChild(image);
      band.appendChild(media);
    } else {
      band.className += ' pdp-glance--no-media';
    }

    var body = make('div', 'pdp-glance__body');
    body.appendChild(make('p', 'pdp-glance__eyebrow', 'At a glance'));

    /* The description's own heading is kept. Only the words "Quick Overview" are tucked away. */
    var title;
    if (headingNode) {
      title = headingNode;
      var full = text(headingNode);
      var short = full.replace(/\s*[\u2013\u2014-]\s*quick\s+overview\s*$/i, '');
      if (short && short !== full) {
        title.textContent = short;
        title.appendChild(make('span', 'pdp-row__heading-hidden', full.slice(short.length)));
      }
      title.removeAttribute('style');
    } else {
      title = make('h2', '', titleText);
    }
    title.className = 'pdp-glance__title';
    body.appendChild(title);

    var facts = make('dl', 'pdp-glance__facts');
    data.facts.forEach(function (fact) {
      var cell = make('div', 'pdp-glance__fact');
      cell.appendChild(make('dt', '', fact.label));
      cell.appendChild(make('dd', '', fact.value));
      facts.appendChild(cell);
    });
    body.appendChild(facts);

    data.notes.slice(0, 2).forEach(function (note) {
      var line = make('p', 'pdp-glance__note');
      line.appendChild(make('strong', '', note.label));
      line.appendChild(document.createTextNode(' ' + note.value.charAt(0).toUpperCase() + note.value.slice(1)));
      body.appendChild(line);
    });

    band.appendChild(body);
    return band;
  }

  var glance = null;
  var overviewData = overview ? factsFromOverview(overview) : null;

  if (overviewData) {
    glance = buildGlance(overviewData, overview.heading, '');
    /* The facts now live in the band, so the original block is taken out of the description. */
    overview.nodes.forEach(function (node) {
      if (node.parentNode) node.parentNode.removeChild(node);
    });
    moved++;
  } else if (made.specs) {
    var specData = factsFromSpecs(made.specs.inner);
    var productTitle = document.querySelector('.product__title');
    if (specData) glance = buildGlance(specData, null, productTitle ? text(productTitle) : 'Key facts');
  }

  if (!moved && !glance) return;

  /* ---------- 2. Product information ---------- */

  ROWS.forEach(function (row) {
    if (made[row.key] && made[row.key].item) list.insertBefore(made[row.key].item, descItem);
  });

  if (made.faqs && !made.faqs.item && askFaqs) {
    askFaqs.hidden = false;
    ask.className += ' pdp-ask--faqs';
  }

  /* Specification tables with two columns become a list of label and value pairs. */
  if (made.specs) {
    slice(made.specs.inner.querySelectorAll('table')).forEach(function (table) {
      var rows = slice(table.querySelectorAll('tr'));
      var pairs = rows.length > 1 && rows.every(function (row) {
        return row.children.length === 2;
      });
      if (pairs) table.classList.add('pdp-specs');
    });
  }

  /*
   * FAQs: each question opens its own answer, one at a time.
   * Descriptions write their questions in different ways: plain headings one after another, a box
   * per question, or a grid of boxes. A question is any h3 (or h4) under the FAQ heading and its answer
   * is whatever follows it up to the next question, so every product ends up as the same tidy list.
   * The boxes the questions came in are dropped once they are empty.
   */
  if (made.faqs) {
    var faqBox = made.faqs.inner;
    var questions = slice(faqBox.querySelectorAll('h3'));
    if (!questions.length) questions = slice(faqBox.querySelectorAll('h4'));
    var questionTag = questions.length ? questions[0].tagName : '';

    questions.forEach(function (question) {
      /* The block directly inside the FAQ area that this question sits in. */
      var holder = question;
      while (holder.parentNode && holder.parentNode !== faqBox) holder = holder.parentNode;
      if (holder.parentNode !== faqBox) return;

      var answer = make('div', 'pdp-faq__a');
      var next = question.nextSibling;
      while (next && !(next.nodeType === 1 && (next.tagName === questionTag || next.querySelector(questionTag)))) {
        var following = next.nextSibling;
        answer.appendChild(next);
        next = following;
      }

      var entry = make('details', 'pdp-faq');
      var summary = make('summary', 'pdp-faq__q');
      faqBox.insertBefore(entry, holder);
      question.removeAttribute('style');
      summary.appendChild(question);
      entry.appendChild(summary);
      entry.appendChild(answer);

      slice(answer.querySelectorAll('[style]')).forEach(function (styled) {
        if (styled.tagName === 'P' || styled.tagName === 'UL' || styled.tagName === 'OL') styled.removeAttribute('style');
      });

      /* Empty boxes left behind, from the innermost outwards. */
      if (holder !== question && holder.parentNode === faqBox && !text(holder) && !holder.querySelector('img, a, table')) {
        faqBox.removeChild(holder);
      }
    });

    /* A box that only held the section's own heading says nothing the title above does not. */
    slice(faqBox.children).forEach(function (child) {
      var own = child.querySelector('h2');
      if (own && text(child) === text(own)) faqBox.removeChild(child);
    });

    /* In the questions section, four or more questions sit in two columns on wide screens. */
    var entries = slice(faqBox.querySelectorAll('.pdp-faq'));
    if (!made.faqs.item && entries.length >= 4) {
      var columns = make('div', 'pdp-faq-cols');
      faqBox.insertBefore(columns, entries[0]);
      var half = Math.ceil(entries.length / 2);
      [entries.slice(0, half), entries.slice(half)].forEach(function (part) {
        var column = make('div', 'pdp-faq-col');
        part.forEach(function (item) {
          column.appendChild(item);
        });
        columns.appendChild(column);
      });
    }

    /* Opening one question closes the others. */
    faqBox.addEventListener('toggle', function (event) {
      var opened = event.target;
      if (!opened.open || !opened.classList || !opened.classList.contains('pdp-faq')) return;
      slice(faqBox.querySelectorAll('.pdp-faq[open]')).forEach(function (other) {
        if (other !== opened) other.open = false;
      });
    }, true);
  }

  /* Tidy the dividing lines left behind: none at the very start or end, and never two in a row. */
  function isBlank(node) {
    return node.nodeType === 3 ? !node.textContent.replace(/\s+/g, '') : node.nodeType !== 1;
  }

  var previousWasLine = true;
  slice(root.childNodes).forEach(function (node) {
    if (isBlank(node)) return;
    if (node.tagName === 'HR') {
      if (previousWasLine) root.removeChild(node);
      previousWasLine = true;
    } else {
      previousWasLine = false;
    }
  });
  var last = root.lastChild;
  while (last && (isBlank(last) || last.tagName === 'HR')) {
    var before = last.previousSibling;
    if (last.tagName === 'HR') root.removeChild(last);
    last = before;
  }

  /* What is left is the full description. If nothing is left, its row goes. */
  var hasContent = inner.textContent.replace(/\s+/g, '') || inner.querySelector('img, iframe, video, table');
  if (!hasContent) {
    list.removeChild(descItem);
  } else {
    var label = descItem.querySelector('.accordion__item-title h3');
    if (label) label.textContent = 'Full description';
  }

  /*
   * A layout for each tab (desktop). Specifications and What's in the box sit beside product photos, and
   * the full description beside a list of its own headings. On phones these extras are hidden and the
   * rows are as before.
   */
  var gallery = slice(document.querySelectorAll('.product__imgs-slider img'));

  /* A column of product photos. It stretches to the height of the tab, each photo taking an equal share. */
  function photos(indexes) {
    var column = make('div', 'pdp-row__media');
    var used = [];

    indexes.forEach(function (index) {
      var source = gallery[index];
      var url = source && (source.getAttribute('src') || source.currentSrc);
      if (!url || used.indexOf(url) > -1) return;
      used.push(url);

      var frame = make('div', 'pdp-row__photo');
      var image = document.createElement('img');
      image.src = url;
      if (source.getAttribute('srcset')) image.setAttribute('srcset', source.getAttribute('srcset'));
      image.setAttribute('sizes', '340px');
      image.alt = '';
      image.loading = 'lazy';
      frame.appendChild(image);
      column.appendChild(frame);
    });

    return used.length ? column : null;
  }

  /* Puts a row's own content in one column and the extra beside it. */
  function split(content, extra, extraFirst) {
    if (!content || !extra) return;
    var main = make('div', 'pdp-row__main');
    while (content.firstChild) main.appendChild(content.firstChild);
    content.classList.add('pdp-row--split');
    if (!extraFirst) content.classList.add('pdp-row--split-end');
    content.appendChild(extraFirst ? extra : main);
    content.appendChild(extraFirst ? main : extra);
  }

  if (made.specs) split(made.specs.inner, photos([2, 3, 4]), true);
  if (made.box) split(made.box.inner, photos([5, 1]), true);

  /* Full description: a list of its own headings to jump between. */
  if (hasContent) {
    var sections = slice(inner.querySelectorAll('h2')).filter(function (heading) {
      return text(heading);
    });
    if (sections.length >= 3) {
      var toc = make('nav', 'pdp-row__aside pdp-toc');
      toc.setAttribute('aria-label', 'In this description');
      toc.appendChild(make('p', 'pdp-toc__title', 'In this description'));
      var links = make('ol', 'pdp-toc__list');
      sections.forEach(function (heading, index) {
        if (!heading.id) heading.id = 'pdp-section-' + (index + 1);
        var item = document.createElement('li');
        var link = make('a', 'pdp-toc__link', text(heading));
        link.href = '#' + heading.id;
        link.addEventListener('click', function (event) {
          event.preventDefault();
          var top = heading.getBoundingClientRect().top + window.pageYOffset - 110;
          if (window.scrollTo) window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        });
        item.appendChild(link);
        links.appendChild(item);
      });
      toc.appendChild(links);
      split(inner, toc, true);
    }
  }

  /* The card that holds the tabs and the rows, with the band above it. */
  var holder = list.parentNode;
  var info = make('div', 'pdp-info');
  holder.insertBefore(info, list);
  if (glance) holder.insertBefore(glance, info);

  var tabs = make('div', 'pdp-tabs');
  tabs.setAttribute('role', 'tablist');
  tabs.setAttribute('aria-label', 'Product information');
  info.appendChild(tabs);
  info.appendChild(list);

  var buttons = [];
  var chosen = null;

  /* The lime bar under the tabs is one bar that slides to the chosen tab. */
  function moveBar() {
    if (!chosen) return;
    tabs.style.setProperty('--pdp-tab-x', chosen.offsetLeft + 'px');
    tabs.style.setProperty('--pdp-tab-w', chosen.offsetWidth + 'px');
  }

  function select(button) {
    chosen = button;
    buttons.forEach(function (other) {
      var on = other === button;
      other.setAttribute('aria-selected', on ? 'true' : 'false');
      other._item.classList[on ? 'add' : 'remove']('is-tab');
    });
    moveBar();
  }

  slice(list.children).forEach(function (item) {
    var heading = item.querySelector('.accordion__item-title h3');
    if (!heading) return;
    var button = make('button', 'pdp-tabs__tab', text(heading));
    button.type = 'button';
    button.setAttribute('role', 'tab');
    button._item = item;
    button.addEventListener('click', function () {
      select(button);
    });
    buttons.push(button);
    tabs.appendChild(button);
  });

  if (buttons.length > 1) {
    info.className += ' pdp-info--tabs';
    tabs.className += ' pdp-tabs--slide';
    select(buttons[0]);
    window.addEventListener('resize', moveBar);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(moveBar);
  } else {
    info.removeChild(tabs);
  }

  /*
   * Motion. Rows and list items rise into place one after another, each column starting together.
   * It plays when the card first scrolls into view and again each time a tab is chosen. The order number
   * set here is what staggers them. Shoppers who ask their device for reduced motion get none of it.
   */
  function stagger(items) {
    var perColumn = Math.ceil(items.length / 2) || 1;
    items.forEach(function (item, index) {
      item.style.setProperty('--pdp-i', String(Math.min(index % perColumn, 14)));
    });
  }

  slice(info.querySelectorAll('table.pdp-specs')).forEach(function (table) {
    stagger(slice(table.querySelectorAll('tr')));
  });
  slice(info.querySelectorAll('.pdp-row--box ul')).forEach(function (listing) {
    stagger(slice(listing.children));
  });
  slice(info.querySelectorAll('.pdp-row--faqs')).forEach(function (faqs) {
    slice(faqs.querySelectorAll('.pdp-faq')).forEach(function (entry, index) {
      entry.style.setProperty('--pdp-i', String(Math.min(index, 14)));
    });
  });

  if ('IntersectionObserver' in window) {
    info.className += ' pdp-info--anim';
    var shown = false;
    var seen = null;

    var reveal = function () {
      if (shown) return;
      shown = true;
      info.className += ' is-inview';
      if (seen) seen.disconnect();
      window.removeEventListener('scroll', check);
    };

    /* A second check on scroll, so the card can never be left waiting if the observer does not report. */
    var check = function () {
      var box = info.getBoundingClientRect();
      if (box.top < window.innerHeight * 0.88 && box.bottom > 0) reveal();
    };

    seen = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) reveal();
      },
      { rootMargin: '0px 0px -12% 0px' }
    );
    seen.observe(info);
    window.addEventListener('scroll', check, { passive: true });
  }
})();

/*
 * Pre-order notice (STOQ / Restock Rocket app). The app writes its notice as lines of text that start with
 * emoji. This shows the same lines as a tick list, and makes the dispatch date bold. The words and the date
 * are the app's own. The app can redraw its block (for example when the option changes), so this runs again
 * whenever the buy buttons area changes.
 */
(function () {
  'use strict';

  var area = document.querySelector('[data-product-buttons]');
  if (!area || !('MutationObserver' in window)) return;

  function plain(html) {
    var holder = document.createElement('div');
    holder.innerHTML = html;
    var value = holder.textContent || '';
    try {
      value = value.replace(new RegExp('[\\u{1F000}-\\u{1FAFF}\\u{2190}-\\u{2BFF}\\u{FE0F}\\u{200D}]', 'gu'), '');
    } catch (e) {}
    return value.replace(/\s+/g, ' ').trim();
  }

  function tidy() {
    var box = area.querySelector('.restock-rocket-preorder-description');
    if (!box) return;

    var words = box.querySelector('.preorder-description-text');
    if (words && !words.querySelector('.pdp-preorder__line')) {
      var lines = words.innerHTML.split(/<br\s*\/?>/i).map(plain).filter(Boolean);
      if (lines.length) {
        words.innerHTML = '';
        lines.forEach(function (line) {
          var row = document.createElement('span');
          row.className = 'pdp-preorder__line';
          row.textContent = line;
          words.appendChild(row);
        });
      }
    }

    Array.prototype.forEach.call(box.querySelectorAll('.preorder-detail-text'), function (detail) {
      if (detail.querySelector('strong')) return;
      var value = detail.textContent || '';
      var at = value.indexOf(':');
      if (at < 1 || at > value.length - 2) return;
      var bold = document.createElement('strong');
      bold.textContent = value.slice(at + 1).trim();
      detail.textContent = value.slice(0, at + 1) + ' ';
      detail.appendChild(bold);
    });
  }

  new MutationObserver(tidy).observe(area, { childList: true, subtree: true });
  tidy();
})();

/*
 * Reassurance points pop-up. On desktop only the first four points show beside a "See more" button.
 * The pop-up's list is copied from the points on the page, so their wording lives in one place
 * (sections/main-product.liquid). The button itself is opened by the pop-up code further up this file.
 */
(function () {
  'use strict';

  var list = document.querySelector('[data-pdp-trust-list]');
  if (!list) return;

  Array.prototype.forEach.call(document.querySelectorAll('.product-trust-badges .product-trust-item'), function (point) {
    var words = point.lastElementChild ? point.lastElementChild.textContent : point.textContent;
    words = (words || '').replace(/\s+/g, ' ').trim();
    if (!words) return;
    var item = document.createElement('li');
    item.textContent = words;
    list.appendChild(item);
  });
})();

/*
 * Product title on phones: one line where possible.
 * The title is shrunk just enough to fit the width of the screen, down to a size that is still easy to read.
 * A title too long for that keeps two lines, at a slightly smaller size than before. Desktop is untouched.
 * It runs again once the heading font has loaded and when the phone is turned.
 */
(function () {
  'use strict';

  var title = document.querySelector('.product__details .product__title');
  if (!title || !window.matchMedia) return;

  var phone = window.matchMedia('(max-width: 768px)');
  var LARGEST = 26;
  var SMALLEST = 18; /* keeps the longest bike names on one line on 360px wide phones too */
  var TWO_LINES = 22;

  function fit() {
    title.style.fontSize = '';
    title.style.whiteSpace = '';
    title.style.overflow = '';
    if (!phone.matches) return;

    title.style.whiteSpace = 'nowrap';
    title.style.fontSize = LARGEST + 'px';
    var room = title.clientWidth;
    var needed = title.scrollWidth;
    if (!room || !needed) {
      title.style.fontSize = '';
      title.style.whiteSpace = '';
      return;
    }

    /*
     * 3px is kept spare. Browsers report these widths as whole numbers, so a title can be a fraction of a
     * pixel wider than it measures, and that fraction was enough to let the whole page scroll sideways.
     */
    var size = needed > room - 3 ? Math.floor((LARGEST * (room - 3) / needed) * 10) / 10 : LARGEST;

    if (size >= SMALLEST) {
      title.style.fontSize = size + 'px';
      /* Belt and braces: whatever happens, the title can never push the page wider than the screen. */
      title.style.overflow = 'hidden';
    } else {
      title.style.whiteSpace = '';
      title.style.fontSize = TWO_LINES + 'px';
    }
  }

  fit();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
  window.addEventListener('load', fit);

  var lastWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    fit();
  });
})();

/*
 * Sabre Digital: desktop gallery.
 *  - A counter on the photo ("3 / 12") that follows whichever photo is open.
 *  - When there are more thumbnails than fit in the row under the photo, the row gets a small arrow at
 *    that end (and fades out there, see Change 28 in sabre-pdp.css).
 *  - The photos after the second are held back while the page opens, then fetched once it has loaded,
 *    so they are already there when the shopper clicks through.
 * The theme still does the sliding and marks the open thumbnail; this only watches for that.
 */
(function () {
  'use strict';

  var gallery = document.querySelector('.pdp-main .product__imgs');
  var nav = gallery && gallery.querySelector('.product__imgs-nav');
  var row = nav && nav.querySelector('.product__imgs-nav-inner');
  var slider = gallery && gallery.querySelector('.product__imgs-slider');
  if (!gallery || !slider) return;

  /* Fetch the held-back photos once the page itself has finished loading. */
  function fetchRest() {
    Array.prototype.forEach.call(slider.querySelectorAll('img[loading="lazy"]'), function (image) {
      image.loading = 'eager';
    });
  }
  if (document.readyState === 'complete') fetchRest();
  else window.addEventListener('load', fetchRest);

  if (!nav || !row) return;
  var thumbs = row.querySelectorAll('.product__imgs-nav-item');
  if (thumbs.length < 2) return;

  var count = document.createElement('span');
  count.className = 'pdp-gallery-count';
  count.setAttribute('aria-hidden', 'true');

  function showCount() {
    var open = 0;
    Array.prototype.forEach.call(thumbs, function (thumb, index) {
      if (thumb.classList.contains('active')) open = index;
    });
    var text = open + 1 + ' / ' + thumbs.length;
    if (count.textContent !== text) count.textContent = text;
  }

  /*
   * The counter sits on the photo, so it goes inside the slider. The slider script treats whatever is
   * inside the slider when it starts as a photo, so the counter is only added once it has started.
   */
  function placeCount() {
    if (count.parentNode || !slider.querySelector('.flickity-viewport')) return;
    slider.appendChild(count);
    showCount();
  }

  /* Which ends of the row have more thumbnails out of view. */
  function markEnds() {
    var before = row.scrollLeft > 4;
    var after = row.scrollLeft + row.clientWidth < row.scrollWidth - 4;
    if (before !== nav.classList.contains('has-before')) nav.classList[before ? 'add' : 'remove']('has-before');
    if (after !== nav.classList.contains('has-after')) nav.classList[after ? 'add' : 'remove']('has-after');
  }

  function arrow(direction) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'pdp-rail-btn pdp-rail-btn--' + direction;
    button.setAttribute('aria-label', direction === 'prev' ? 'Earlier photos' : 'More photos');
    button.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="' +
      (direction === 'prev' ? 'M8 2L4 6l4 4' : 'M4 2l4 4-4 4') +
      '"/></svg>';
    button.addEventListener('click', function () {
      var step = Math.max(row.clientWidth - 90, 120);
      row.scrollLeft += direction === 'prev' ? -step : step;
    });
    nav.appendChild(button);
  }

  arrow('prev');
  arrow('next');

  row.addEventListener('scroll', markEnds, { passive: true });
  window.addEventListener('resize', markEnds);
  window.addEventListener('load', function () {
    placeCount();
    markEnds();
  });

  if ('MutationObserver' in window) {
    new MutationObserver(showCount).observe(row, { attributes: true, subtree: true, attributeFilter: ['class'] });
    new MutationObserver(placeCount).observe(slider, { childList: true });
  }

  placeCount();
  showCount();
  markEnds();
})();
